import { imageUrlForKey } from "../stitch-bind";
import {
  collectionsByRecent,
  collectionsForGrid,
  featuredCollections,
} from "../content/collections";
import type { CollectionEntry } from "../content/types";

function buildCarouselForCollections(items: readonly CollectionEntry[]): HTMLElement {
  const shell = document.createElement("div");
  shell.className = "carousel-shell carousel-shell--collections";
  shell.dataset.carouselShell = "";

  const bar = document.createElement("div");
  bar.className = "carousel-shell__bar carousel-shell__bar--collections";

  const prev = document.createElement("button");
  prev.type = "button";
  prev.className = "carousel-shell__nav-btn";
  prev.dataset.carouselPrev = "";
  prev.setAttribute("aria-label", "Colecciones anteriores");
  prev.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">chevron_left</span>';

  const track = document.createElement("div");
  track.className = "carousel-track carousel-track--collections";
  track.dataset.carouselTrack = "";
  track.setAttribute("aria-label", "Últimas colecciones Hikarai");

  const next = document.createElement("button");
  next.type = "button";
  next.className = "carousel-shell__nav-btn";
  next.dataset.carouselNext = "";
  next.setAttribute("aria-label", "Colecciones siguientes");
  next.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">chevron_right</span>';

  for (const col of items) {
    const card = document.createElement("a");
    card.href = col.href;
    card.className = "collection-carousel-card carousel-track__item";

    const media = document.createElement("div");
    media.className = "collection-carousel-card__media";

    const img = document.createElement("img");
    img.src = imageUrlForKey(col.imageKey);
    img.alt = col.name;
    img.loading = "lazy";
    img.decoding = "async";

    const shade = document.createElement("div");
    shade.className = "collection-carousel-card__shade";
    shade.setAttribute("aria-hidden", "true");

    const body = document.createElement("div");
    body.className = "collection-carousel-card__body";

    const h = document.createElement("h2");
    h.className = "collection-carousel-card__title";
    h.textContent = col.name;

    const pitch = document.createElement("p");
    pitch.className = "collection-carousel-card__pitch";
    pitch.textContent = col.pitch;

    const vibe = document.createElement("p");
    vibe.className = "collection-carousel-card__vibe";
    vibe.textContent = col.vibeLine;

    const series = document.createElement("span");
    series.className = "collection-carousel-card__tone";
    series.textContent = col.seriesHint;

    media.appendChild(img);
    media.appendChild(shade);
    body.appendChild(h);
    body.appendChild(pitch);
    body.appendChild(vibe);
    body.appendChild(series);
    card.appendChild(media);
    card.appendChild(body);
    track.appendChild(card);
  }

  bar.appendChild(prev);
  bar.appendChild(track);
  bar.appendChild(next);
  shell.appendChild(bar);
  return shell;
}

function buildFeaturedSection(items: readonly CollectionEntry[]): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "collections-featured-list";

  for (const col of items) {
    const article = document.createElement("article");
    article.className = "collection-featured-card";

    const mediaLink = document.createElement("a");
    mediaLink.href = col.href;
    mediaLink.className = "collection-featured-card__media";
    mediaLink.setAttribute("aria-label", `Ver ${col.name}`);

    const figure = document.createElement("figure");
    figure.className = "collection-featured-card__figure";

    const img = document.createElement("img");
    img.src = imageUrlForKey(col.imageKey);
    img.alt = "";
    img.loading = "lazy";
    img.decoding = "async";

    figure.appendChild(img);
    mediaLink.appendChild(figure);

    const body = document.createElement("div");
    body.className = "collection-featured-card__body";

    const title = document.createElement("h3");
    title.className = "collection-featured-card__title";

    const titleLink = document.createElement("a");
    titleLink.href = col.href;
    titleLink.textContent = col.name;
    title.appendChild(titleLink);

    const series = document.createElement("p");
    series.className = "collection-featured-card__series";
    series.textContent = col.seriesHint;

    const pitch = document.createElement("p");
    pitch.className = "collection-featured-card__pitch";
    pitch.textContent = col.pitch;

    const desc = document.createElement("p");
    desc.className = "collection-featured-card__desc";
    desc.textContent = col.featuredDescription;

    const cta = document.createElement("a");
    cta.href = col.href;
    cta.className = "link-secondary-ghost collection-featured-card__cta";
    cta.textContent = "Ver piezas de esta línea";

    body.appendChild(title);
    body.appendChild(series);
    body.appendChild(pitch);
    body.appendChild(desc);
    body.appendChild(cta);

    article.appendChild(mediaLink);
    article.appendChild(body);
    wrap.appendChild(article);
  }

  return wrap;
}

function buildCollectionsGrid(items: readonly CollectionEntry[]): HTMLElement {
  const grid = document.createElement("div");
  grid.className = "collections-grid";

  for (const col of items) {
    const card = document.createElement("a");
    card.href = col.href;
    card.className = "collection-card";

    const figure = document.createElement("figure");
    figure.className = "collection-card__media";

    const img = document.createElement("img");
    img.src = imageUrlForKey(col.imageKey);
    img.alt = col.name;
    img.width = 900;
    img.height = 1200;
    img.loading = "lazy";
    img.decoding = "async";

    figure.appendChild(img);

    const shade = document.createElement("div");
    shade.className = "collection-card__shade";
    shade.setAttribute("aria-hidden", "true");

    const body = document.createElement("div");
    body.className = "collection-card__body";

    const h = document.createElement("h2");
    h.className = "collection-card__title";
    h.textContent = col.name;

    const meta = document.createElement("p");
    meta.className = "collection-card__meta";
    meta.textContent = `${col.seriesHint} · ${col.pitch}`;

    body.appendChild(h);
    body.appendChild(meta);

    card.appendChild(figure);
    card.appendChild(shade);
    card.appendChild(body);
    grid.appendChild(card);
  }

  return grid;
}

export function renderColeccionesPage(): void {
  const carouselRoot = document.querySelector("[data-collections-carousel-mount]");
  if (carouselRoot instanceof HTMLElement) {
    carouselRoot.textContent = "";
    carouselRoot.appendChild(buildCarouselForCollections(collectionsByRecent()));
  }

  const featuredRoot = document.querySelector("[data-collections-featured-mount]");
  if (featuredRoot instanceof HTMLElement) {
    featuredRoot.textContent = "";
    const list = featuredCollections();
    if (list.length > 0) {
      featuredRoot.appendChild(buildFeaturedSection(list));
    }
  }

  const gridRoot = document.querySelector("[data-collections-grid-mount]");
  if (gridRoot instanceof HTMLElement) {
    gridRoot.textContent = "";
    gridRoot.appendChild(buildCollectionsGrid(collectionsForGrid()));
  }
}
